import { PromptCategoryId } from "@shaarp/common";

export type ColorScheme = {
  border: string;
  hoverBorder: string;
  bg: string;
  hoverBg: string;
  icon: string;
  iconBg: string;
  badge?: string;
  shadow?: string;
};

export const CATEGORY_COLORS: Record<PromptCategoryId, ColorScheme> = {
  targeting: {
    border: "border-emerald-200 dark:border-emerald-900/50",
    hoverBorder: "hover:border-emerald-300 dark:hover:border-emerald-800",
    bg: "bg-emerald-50/50 dark:bg-emerald-950/20",
    hoverBg: "hover:bg-emerald-100/50 dark:hover:bg-emerald-900/30",
    icon: "text-emerald-600 dark:text-emerald-400",
    iconBg: "bg-emerald-100/80 dark:bg-emerald-900/30",
    badge:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
    shadow: "shadow-[0_0_20px_-4px] shadow-emerald-400/40 dark:shadow-[0_0_25px_-2px] dark:shadow-emerald-400/50",
  },
  brief: {
    border: "border-indigo-200 dark:border-indigo-900/50",
    hoverBorder: "hover:border-indigo-300 dark:hover:border-indigo-800",
    bg: "bg-indigo-50/50 dark:bg-indigo-950/20",
    hoverBg: "hover:bg-indigo-100/50 dark:hover:bg-indigo-900/30",
    icon: "text-indigo-600 dark:text-indigo-400",
    iconBg: "bg-indigo-100/80 dark:bg-indigo-900/30",
    badge:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300",
    shadow: "shadow-[0_0_20px_-4px] shadow-indigo-400/40 dark:shadow-[0_0_25px_-2px] dark:shadow-indigo-400/50",
  },
  signals: {
    border: "border-amber-200 dark:border-amber-900/50",
    hoverBorder: "hover:border-amber-300 dark:hover:border-amber-800",
    bg: "bg-amber-50/50 dark:bg-amber-950/20",
    hoverBg: "hover:bg-amber-100/50 dark:hover:bg-amber-900/30",
    icon: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-100/80 dark:bg-amber-900/30",
    badge:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
    shadow: "shadow-[0_0_20px_-4px] shadow-amber-400/40 dark:shadow-[0_0_25px_-2px] dark:shadow-amber-400/50",
  },
  approach: {
    border: "border-pink-200 dark:border-pink-900/50",
    hoverBorder: "hover:border-pink-300 dark:hover:border-pink-800",
    bg: "bg-pink-50/50 dark:bg-pink-950/20",
    hoverBg: "hover:bg-pink-100/50 dark:hover:bg-pink-900/30",
    icon: "text-pink-600 dark:text-pink-400",
    iconBg: "bg-pink-100/80 dark:bg-pink-900/30",
    badge: "bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300",
    shadow: "shadow-[0_0_20px_-4px] shadow-pink-400/40 dark:shadow-[0_0_25px_-2px] dark:shadow-pink-400/50",
  },
  writing: {
    border: "border-violet-200 dark:border-violet-900/50",
    hoverBorder: "hover:border-violet-300 dark:hover:border-violet-800",
    bg: "bg-violet-50/50 dark:bg-violet-950/20",
    hoverBg: "hover:bg-violet-100/50 dark:hover:bg-violet-900/30",
    icon: "text-violet-600 dark:text-violet-400",
    iconBg: "bg-violet-100/80 dark:bg-violet-900/30",
    badge:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300",
    shadow: "shadow-[0_0_20px_-4px] shadow-violet-400/40 dark:shadow-[0_0_25px_-2px] dark:shadow-violet-400/50",
  },
  qualification: {
    border: "border-cyan-200 dark:border-cyan-900/50",
    hoverBorder: "hover:border-cyan-300 dark:hover:border-cyan-800",
    bg: "bg-cyan-50/50 dark:bg-cyan-950/20",
    hoverBg: "hover:bg-cyan-100/50 dark:hover:bg-cyan-900/30",
    icon: "text-cyan-600 dark:text-cyan-400",
    iconBg: "bg-cyan-100/80 dark:bg-cyan-900/30",
    badge: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300",
    shadow: "shadow-[0_0_20px_-4px] shadow-cyan-400/40 dark:shadow-[0_0_25px_-2px] dark:shadow-cyan-400/50",
  },
  other: {
    border: "border-slate-200 dark:border-slate-900/50",
    hoverBorder: "hover:border-slate-300 dark:hover:border-slate-800",
    bg: "bg-slate-50/50 dark:bg-slate-950/20",
    hoverBg: "hover:bg-slate-100/50 dark:hover:bg-slate-900/30",
    icon: "text-slate-600 dark:text-slate-400",
    iconBg: "bg-slate-100/80 dark:bg-slate-900/30",
    badge:
      "bg-slate-100 text-slate-700 dark:bg-slate-900/50 dark:text-slate-300",
    shadow: "shadow-[0_0_20px_-4px] shadow-slate-400/40 dark:shadow-[0_0_25px_-2px] dark:shadow-slate-400/50",
  },
};

// Colors for custom prompts (user/team)
export const OWNER_COLORS: Record<string, ColorScheme> = {
  user: {
    border: "border-blue-200 dark:border-blue-900/50",
    hoverBorder: "hover:border-blue-300 dark:hover:border-blue-800",
    bg: "bg-blue-50/50 dark:bg-blue-950/20",
    hoverBg: "hover:bg-blue-100/50 dark:hover:bg-blue-900/30",
    icon: "text-blue-600 dark:text-blue-400",
    iconBg: "bg-blue-100/80 dark:bg-blue-900/30",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
    shadow: "shadow-[0_0_20px_-4px] shadow-blue-400/40 dark:shadow-[0_0_25px_-2px] dark:shadow-blue-400/50",
  },
  team: {
    border: "border-purple-200 dark:border-purple-900/50",
    hoverBorder: "hover:border-purple-300 dark:hover:border-purple-800",
    bg: "bg-purple-50/50 dark:bg-purple-950/20",
    hoverBg: "hover:bg-purple-100/50 dark:hover:bg-purple-900/30",
    icon: "text-purple-600 dark:text-purple-400",
    iconBg: "bg-purple-100/80 dark:bg-purple-900/30",
    badge:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300",
    shadow: "shadow-[0_0_20px_-4px] shadow-purple-400/40 dark:shadow-[0_0_25px_-2px] dark:shadow-purple-400/50",
  },
  public: {
    border: "border-border",
    hoverBorder: "hover:border-border/50",
    bg: "",
    hoverBg: "hover:bg-accent/50",
    icon: "text-muted-foreground",
    iconBg: "bg-muted/50 dark:bg-muted/30",
    badge: "bg-secondary text-secondary-foreground",
    shadow: "shadow-[0_0_20px_-4px] shadow-border/40",
  },
};
